import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import Footer from "../Shared/Footer/Footer";
import Navigation from "../Shared/Navigation/Navigation";

const Login = () => {
  const [loginData, setLoginData] = useState({});
  const { user, loginUser, signInWithGoogle, isLoading, authError } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setLoginData({ ...loginData, [name]: value });
  };

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    loginUser(loginData.email, loginData.password, location);
  };

  const handleGoogleSignIn = () => {
    signInWithGoogle(location);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navigation />

      <main className="flex-grow flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-4xl flex rounded-3xl overflow-hidden shadow-xl bg-white">
          {/* Left Panel — decorative (hidden on mobile) */}
          <div className="hidden md:flex w-5/12 bg-slate-900 flex-col items-center justify-center p-10 text-white text-center">
            <span className="text-6xl mb-5">🐾</span>
            <h2 className="text-2xl font-bold mb-3">Welcome Back!</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              Sign in to manage your bookings, track shipments, and explore
              our newest furry friends.
            </p>
          </div>

          {/* Right Panel — form */}
          <div className="w-full md:w-7/12 p-8 sm:p-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-1">
              Sign In
            </h2>
            <p className="text-sm text-slate-400 mb-8">
              Don&apos;t have an account?{" "}
              <Link to="/register" className="text-amber-500 font-semibold hover:underline">
                Register now
              </Link>
            </p>

            {isLoading ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <form onSubmit={handleLoginSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-slate-500 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    onChange={handleOnChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-xs font-medium text-slate-500 mb-1.5">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    id="password"
                    onChange={handleOnChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-amber-500 hover:text-slate-900 transition-all duration-300 mt-2"
                >
                  Login
                </button>

                <div className="relative my-2 flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-slate-400">or</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="w-full py-2.5 border border-gray-200 text-slate-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 48 48">
                    <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 29.8 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 5.1 29.6 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.5 0 20-7.6 20-21 0-1.4-.1-2.7-.5-4z"/>
                    <path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 5.1 29.6 3 24 3c-7.6 0-14.2 4.5-17.7 11.7z"/>
                    <path fill="#FBBC05" d="M24 45c5.8 0 10.6-1.9 14.2-5.1l-6.7-5.4C29.8 36.1 27 37 24 37c-5.8 0-10.7-3.1-11.8-7.5l-7 5.4C8 41.4 15.4 45 24 45z"/>
                    <path fill="#EA4335" d="M44.5 20H24v8.5h11.8c-.6 2.7-2.3 5-4.8 6.5l6.7 5.4C41.8 37.7 44.5 31.3 44.5 24c0-1.4-.1-2.7-.5-4z"/>
                  </svg>
                  Continue with Google
                </button>
              </form>
            )}

            {user?.email && (
              <p className="mt-6 text-center text-green-600 text-sm font-semibold">
                ✓ Login successful!
              </p>
            )}
            {authError && (
              <p className="mt-4 text-center text-red-500 text-sm">{authError}</p>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Login;
